import React, { useState } from "react";
import axios from "axios";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography
} from "@material-ui/core";

const ADDRESS_FIELDS = ['address_1', 'address_2', 'city', 'state', 'zipcode'];

const EMPTY_ADDRESS = {
  address_1: '', address_2: '', city: '', state: '', zipcode: '', county: '', source_url: '', last_validated_at: ''
};

const fieldsFrom = (homeAddress) => (homeAddress ? {
  address_1: homeAddress.address_1 || '',
  address_2: homeAddress.address_2 || '',
  city: homeAddress.city || '',
  state: homeAddress.state || '',
  zipcode: homeAddress.zipcode || '',
  county: homeAddress.county || '',
  source_url: homeAddress.source_url || '',
  last_validated_at: homeAddress.last_validated_at ? String(homeAddress.last_validated_at).slice(0, 10) : ''
} : EMPTY_ADDRESS);

const HomeAddressModal = ({ contact, homeAddress, adminUserSession, onClose, onSaved }) => {
  const editing = !!homeAddress;
  const [initialFields] = useState(() => fieldsFrom(homeAddress));
  const [fields, setFields] = useState(initialFields);
  const [validation, setValidation] = useState(null);
  const [errors, setErrors] = useState([]);
  const [failureDetails, setFailureDetails] = useState(null);
  const [busy, setBusy] = useState(false);

  const baseUrl = `/v1/admin/ria_contacts/${contact.contact_crd_id}/home_addresses`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-User-Email': adminUserSession.email,
      'X-Admin-User-Token': adminUserSession.token
    }
  };

  const addressChanged = ADDRESS_FIELDS.some((field) => fields[field] !== initialFields[field]);
  const anythingChanged = Object.keys(fields).some((field) => fields[field] !== initialFields[field]);
  // A new or changed address has to pass the validation; the other fields of an existing one can be saved as they are
  const canSave = !busy && (validation ? true : editing && !addressChanged && anythingChanged);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
    // Editing a validated field invalidates the validation
    if (ADDRESS_FIELDS.includes(name)) setValidation(null);
    setErrors([]);
    setFailureDetails(null);
  };

  const messagesFrom = (error) => error.response?.data?.errors || [error.response?.data?.error || 'Something went wrong'];

  // The server answers the class and message of an unexpected failure: show them and log the whole answer
  const reportFailure = (action, error) => {
    const details = error.response?.data?.details;
    console.error(`Home address ${action} failed`, error.response?.data || error);
    setErrors(messagesFrom(error));
    setFailureDetails(details ? `${details.error_class}: ${details.error_message}` : null);
  };

  const handleValidate = () => {
    setBusy(true);
    setErrors([]);
    setFailureDetails(null);
    axios.post(`${baseUrl}/validate`, { home_address: fields }, config)
      .then((response) => {
        if (response.data.valid) {
          setValidation({ token: response.data.token, distance: response.data.distance_from_work });
          // The ZIP Smarty found fills the blank one. It does not clear the validation: the token was signed with it
          if (response.data.zipcode) setFields((current) => ({ ...current, zipcode: response.data.zipcode }));
        } else {
          setValidation(null);
          setErrors(response.data.errors);
        }
      })
      .catch((error) => reportFailure('validation', error))
      .finally(() => setBusy(false));
  };

  const handleSave = () => {
    setBusy(true);
    setErrors([]);
    const body = { home_address: fields, validation_token: validation?.token };
    const request = editing ? axios.put(`${baseUrl}/${homeAddress.id}`, body, config) : axios.post(baseUrl, body, config);
    request
      .then(() => onSaved())
      .catch((error) => { reportFailure('save', error); setBusy(false); });
  };

  const textField = (name, label, extra = {}) => (
    <Grid item xs={12} sm={extra.sm || 6}>
      <TextField
        fullWidth
        variant='outlined'
        size='small'
        name={name}
        label={label}
        value={fields[name]}
        onChange={handleChange}
        InputLabelProps={extra.type === 'date' ? { shrink: true } : undefined}
        {...extra}
      />
    </Grid>
  );

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{editing ? 'Edit Home Address' : 'Add Home Address'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {textField('address_1', 'Street 1')}
          {textField('address_2', 'Street 2')}
          {textField('city', 'City')}
          {textField('state', 'State', { sm: 3 })}
          {textField('zipcode', 'ZIP Code', { sm: 3 })}
          {textField('county', 'County')}
          {textField('last_validated_at', 'Last Validated', { type: 'date' })}
          {textField('source_url', 'Source URL', { sm: 12 })}
        </Grid>
        {validation ? (
          <Typography style={{ marginTop: 16, color: '#2e7d32' }}>
            Address validated{validation.distance ? ` - distance from work: ${validation.distance}` : ''}
          </Typography>
        ) : null}
        {errors.map((message) => (
          <Typography key={message} style={{ marginTop: 8, color: '#d32f2f' }}>{message}</Typography>
        ))}
        {failureDetails ? (
          <Typography variant='caption' style={{ display: 'block', marginTop: 4, color: '#757575', wordBreak: 'break-word' }}>{failureDetails}</Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleValidate} disabled={busy || (editing && !addressChanged)}>Validate</Button>
        <Button color='primary' variant='contained' onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HomeAddressModal;
